import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ParameterConfig } from '@/types/mcpTool';
import { Link, Search, Plus, Trash2, ArrowUpRight } from 'lucide-react';
import { ParameterField } from './ParameterField';
import { InsertParameterDialog } from './InsertParameterDialog';

interface ParameterBuilderProps {
  type: 'path' | 'query';
  values: Record<string, any>;
  configs: Record<string, ParameterConfig>;
  onChange: (values: Record<string, any>, configs: Record<string, ParameterConfig>) => void;
  endpointParams?: string[]; // Parameters auto-detected from endpoint
  currentEndpoint?: string; // Current endpoint URL
  onEndpointUpdate?: (newEndpoint: string) => void; // Callback to update endpoint
}

/**
 * ParameterBuilder Component
 *
 * Allows users to define path or query parameters with "Add Field" functionality
 * Features:
 * - Add/remove parameter fields
 * - Each field has name input + ParameterField with 3 checkboxes
 * - Helps construct endpoint URL with {} syntax
 */
export const ParameterBuilder: React.FC<ParameterBuilderProps> = ({
  type,
  values,
  configs,
  onChange,
  endpointParams = [],
  currentEndpoint = '',
  onEndpointUpdate,
}) => {
  const [customParams, setCustomParams] = useState<Array<{ key: string }>>([]);
  const [insertDialogOpen, setInsertDialogOpen] = useState(false);
  const [paramToInsert, setParamToInsert] = useState<string>('');

  const handleFieldChange = (fieldName: string, value: any, config: ParameterConfig) => {
    const newValues = { ...values, [fieldName]: value };
    const newConfigs = { ...configs, [fieldName]: config };
    onChange(newValues, newConfigs);
  };

  const addParameter = () => {
    setCustomParams([...customParams, { key: '' }]);
  };

  const removeParameter = (index: number) => {
    const paramToRemove = customParams[index];
    const newCustomParams = customParams.filter((_, i) => i !== index);
    setCustomParams(newCustomParams);

    if (paramToRemove.key) {
      const newValues = { ...values };
      const newConfigs = { ...configs };
      delete newValues[paramToRemove.key];
      delete newConfigs[paramToRemove.key];
      onChange(newValues, newConfigs);
    }
  };

  const handleParamNameChange = (index: number, newKey: string) => {
    const oldKey = customParams[index].key;
    const newCustomParams = [...customParams];
    newCustomParams[index].key = newKey;
    setCustomParams(newCustomParams);

    // If key changed and old key had values, remove old key
    if (oldKey && oldKey !== newKey) {
      const newValues = { ...values };
      const newConfigs = { ...configs };
      delete newValues[oldKey];
      delete newConfigs[oldKey];

      // Initialize new key if it doesn't exist
      if (newKey && !newConfigs[newKey]) {
        newConfigs[newKey] = {
          source: 'direct_input',
          fallback: 'error',
          required: false,
          let_ai_decide: false,
          handoff_to_human: false,
        };
      }

      onChange(newValues, newConfigs);
    }
  };

  const handleInsertClick = (paramKey: string) => {
    setParamToInsert(paramKey);
    setInsertDialogOpen(true);
  };

  const handleEndpointInsert = (newEndpoint: string) => {
    if (onEndpointUpdate) {
      onEndpointUpdate(newEndpoint);
    }
  };

  const getIcon = () => {
    return type === 'path' ? <Link className="h-4 w-4 text-blue-600" /> : <Search className="h-4 w-4 text-green-600" />;
  };

  const getConfig = (fieldName: string): ParameterConfig => {
    return configs[fieldName] || {
      source: 'direct_input',
      fallback: 'error',
      required: false,
      let_ai_decide: false,
      handoff_to_human: false,
    };
  };

  // Separate endpoint-detected params from manual params
  const manualParamKeys = customParams.map(p => p.key).filter(Boolean);
  const allParamKeys = [...endpointParams, ...manualParamKeys];
  const uniqueParamKeys = Array.from(new Set(allParamKeys));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {getIcon()}
          <h4 className="text-sm font-medium">
            {type === 'path' ? 'URL Path Parameters' : 'Query Parameters'}
          </h4>
          <Badge variant="outline" className="text-xs">
            {uniqueParamKeys.length} parameter{uniqueParamKeys.length !== 1 ? 's' : ''}
          </Badge>
          {endpointParams.length > 0 && (
            <Badge variant="secondary" className="text-xs bg-blue-50 text-blue-700">
              {endpointParams.length} from endpoint
            </Badge>
          )}
        </div>
      </div>

      {/* Help Text */}
      {customParams.length === 0 && uniqueParamKeys.length === 0 && (
        <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed">
          {getIcon()}
          <p className="text-sm text-gray-600 mb-1 mt-2">
            No {type === 'path' ? 'path' : 'query'} parameters yet
          </p>
          <p className="text-xs text-gray-500">
            {type === 'path'
              ? 'Add {customer_id} in endpoint URL or click "Add Parameter" below'
              : 'Add ?status={status} in endpoint URL or click "Add Parameter" below'}
          </p>
        </div>
      )}

      {/* Parameters from Endpoint */}
      {endpointParams.length > 0 && (
        <div className="space-y-3">
          <h5 className="text-xs font-medium text-gray-600 flex items-center gap-2">
            <Badge variant="secondary" className="text-xs bg-blue-50 text-blue-700">From Endpoint</Badge>
          </h5>
          {endpointParams.map((paramKey) => (
            <ParameterField
              key={paramKey}
              name={paramKey}
              type={type}
              value={values[paramKey] || ''}
              config={getConfig(paramKey)}
              onChange={(value, config) => handleFieldChange(paramKey, value, config)}
            />
          ))}
        </div>
      )}

      {/* Custom Parameter Fields */}
      {customParams.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h5 className="text-xs font-medium text-gray-600 flex items-center gap-2">
              <Badge variant="secondary" className="text-xs">Manually Added</Badge>
            </h5>
          </div>
          {customParams.map((param, index) => (
            <div key={index} className="space-y-2">
              <div className="flex items-center gap-2">
                <Input
                  placeholder={`Parameter name (e.g., ${type === 'path' ? 'customer_id' : 'status'})`}
                  value={param.key}
                  onChange={(e) => handleParamNameChange(index, e.target.value)}
                  className="flex-1 font-medium"
                />
                {param.key && !endpointParams.includes(param.key) && onEndpointUpdate && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleInsertClick(param.key)}
                    className="text-blue-600 hover:text-blue-700"
                    title="Insert into endpoint"
                  >
                    <ArrowUpRight className="h-4 w-4" />
                  </Button>
                )}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeParameter(index)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              {param.key && (
                <ParameterField
                  name={param.key}
                  type={type}
                  value={values[param.key] || ''}
                  config={getConfig(param.key)}
                  onChange={(value, config) => handleFieldChange(param.key, value, config)}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add Parameter Button */}
      <Button
        type="button"
        variant="outline"
        size="lg"
        onClick={addParameter}
        className="w-full"
      >
        <Plus className="h-5 w-5 mr-2" />
        Add Parameter
      </Button>

      {/* Helper: Show endpoint syntax for manually added params */}
      {manualParamKeys.length > 0 && !onEndpointUpdate && (
        <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg">
          <p className="text-xs font-medium text-purple-900 mb-2">
            💡 {type === 'path' ? 'To use in endpoint URL path:' : 'To use in endpoint query string:'}
          </p>
          <code className="text-xs text-purple-700 font-mono block break-all">
            {type === 'path'
              ? '/' + manualParamKeys.map(k => `{${k}}`).join('/')
              : '?' + manualParamKeys.map(k => `${k}={${k}}`).join('&')}
          </code>
          <p className="text-xs text-purple-600 mt-2">
            Copy this and add to your endpoint URL above
          </p>
        </div>
      )}

      {/* Insert Parameter Dialog */}
      <InsertParameterDialog
        open={insertDialogOpen}
        onOpenChange={setInsertDialogOpen}
        parameterName={paramToInsert}
        parameterType={type}
        currentEndpoint={currentEndpoint}
        onInsert={handleEndpointInsert}
      />
    </div>
  );
};
